import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import notify, { MESSAGES } from '../../../utils/notify';
import classnames from 'classnames';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import { formatDateToDDMMYYYY } from '../../../utils/dateformatter';
import ActionButton from '../../../Components/Common/ActionButton';
import { EmptyState } from '../../../Components/Common/ListStates';
import { usePermission } from '../../../contexts/PermissionContext';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import { fetchJson } from '../../Procurement/utils/procurementList';
import { ProductAvatar } from '../../Procurement/component/ListBadges';
import '../../TenderOpening/components/TenderOpening.css';
import '../../Procurement/ProcurementView.css';
import {
  STATUS_TABS,
  DEFAULT_STATUS_TAB,
  getStatusValueByTabId,
} from './StatusTabs';
import {
  DEFAULT_SORT_FIELD,
  HPS_PROCUREMENT_TYPE_MAP,
  HPS_SORT_FIELDS,
  buildHpsListUrl,
  exportHpsList,
  isHpsAvailable,
} from '../utils/hpsList';
import { getProcurementStatusConfig } from '../utils/hpsHelpers';
import StatusBadge from '../../../Components/Common/StatusBadge';
import { getListProductTypeLabel } from '../../../shared/offer-domain/constants';

// `tabId` is a status tab id from StatusTabs ('' = no status filter).
const STAT_CARDS = [
  {
    key: 'total',
    tabId: '',
    label: 'Total HPS',
    icon: 'ri-stack-line',
    tone: 'total',
  },
  {
    key: 'notAvailable',
    tabId: 'notAvailable',
    label: 'Not Available',
    icon: 'ri-close-circle-line',
    tone: 'progress',
  },
  {
    key: 'revise',
    tabId: 'revise',
    label: 'Revise',
    icon: 'ri-error-warning-line',
    tone: 'open',
  },
  {
    key: 'available',
    tabId: 'available',
    label: 'Available',
    icon: 'ri-checkbox-circle-line',
    tone: 'done',
  },
];

const StatusPill = ({ row }) => {
  const status = getProcurementStatusConfig(row?.statusHPS2);
  return <StatusBadge tone={status.tone} label={status.label} />;
};

const ProductInfo = ({ item }) => {
  const name = item?.productType?.name;
  const sub = getListProductTypeLabel(item, item?.productType?.description);
  if (!name && !sub) return null;

  return (
    <div className='to-product'>
      <span className='to-product-name'>{name || '-'}</span>
      {sub && <span className='text-truncate ms-2'>· {sub}</span>}
    </div>
  );
};

const StatCard = ({ card, summary, isActive, onSelect }) => {
  const total = summary?.total || 0;
  const count = summary?.[card.key];
  const share =
    card.key !== 'total' && total > 0 && typeof count === 'number'
      ? Math.round((count / total) * 100)
      : null;

  return (
    <button
      type='button'
      className={classnames('to-stat', `to-stat--${card.tone}`, {
        'is-active': isActive,
      })}
      aria-pressed={isActive}
      onClick={onSelect}
    >
      <span className='to-stat-icon'>
        <i className={card.icon} aria-hidden='true' />
      </span>
      <span>
        <span className='to-stat-label'>{card.label}</span>
        <span className='to-stat-value'>
          {typeof count === 'number' ? count : '–'}
        </span>
      </span>
      {share !== null && <span className='to-stat-share'>{share}%</span>}
    </button>
  );
};

const SkeletonRows = ({ count = 5 }) => (
  <div className='w-100 px-3 py-2'>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className='d-flex align-items-center gap-3 py-3'>
        <div className='to-skeleton' style={{ width: '9%' }} />
        <div className='to-skeleton' style={{ width: '26%' }} />
        <div className='to-skeleton' style={{ width: '12%' }} />
        <div className='to-skeleton' style={{ width: '18%' }} />
        <div className='to-skeleton flex-grow-1' />
      </div>
    ))}
  </div>
);

const HpsTable = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  exportName = 'List',
}) => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [statusTab, setStatusTab] = useState(DEFAULT_STATUS_TAB);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState('desc');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const statusValue = getStatusValueByTabId(statusTab);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);

    fetchJson(
      buildHpsListUrl({
        currentPage,
        rowsPerPage,
        procurementTypeHeader,
        search: debouncedSearchText,
        statusValue,
        sortField,
        sortDirection,
      }),
      token,
      controller.signal
    )
      .then(result => {
        setItems(result?.data?.items || []);
        setTotalRows(result?.data?.totalItems || 0);
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        notify.apiError(error, 'load procurement data');
        setItems([]);
        setTotalRows(0);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    statusValue,
    sortField,
    sortDirection,
    procurementTypeHeader,
    refreshTrigger,
  ]);

  // Counts come from the list endpoint with PageSize=1, reading totalItems.
  useEffect(() => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) return undefined;

    const controller = new AbortController();

    Promise.all(
      STAT_CARDS.map(async card => {
        const result = await fetchJson(
          buildHpsListUrl({
            currentPage: 1,
            rowsPerPage: 1,
            procurementTypeHeader,
            statusValue: getStatusValueByTabId(card.tabId),
          }),
          token,
          controller.signal
        );
        return [card.key, result?.data?.totalItems || 0];
      })
    )
      .then(entries => setSummary(Object.fromEntries(entries)))
      .catch(error => {
        // The list request already reports load failures; cards just hide counts.
        if (error.name !== 'AbortError') setSummary(null);
      });

    return () => controller.abort();
  }, [procurementTypeHeader, refreshTrigger]);

  const handleStatusFilter = tabId => {
    setStatusTab(tabId);
    setCurrentPage(1);
  };

  const handleSort = useCallback((column, direction) => {
    setSortField(
      HPS_SORT_FIELDS[column.id] || column.id || 'procurementnumber'
    );
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  const handlePerRowsChange = newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchText('');
    setStatusTab(DEFAULT_STATUS_TAB);
    setCurrentPage(1);
  };

  // Exports every row matching the current filters, not just this page.
  const handleExport = async format => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    setIsExporting(true);
    try {
      const query = {
        currentPage: 1,
        procurementTypeHeader,
        search: debouncedSearchText,
        statusValue,
        sortField,
        sortDirection,
      };
      let result = await fetchJson(
        buildHpsListUrl({ ...query, rowsPerPage: Math.max(totalRows, 1) }),
        token
      );
      const total = result?.data?.totalItems || 0;
      if (total > (result?.data?.items?.length || 0)) {
        result = await fetchJson(
          buildHpsListUrl({ ...query, rowsPerPage: total }),
          token
        );
      }

      const rows = result?.data?.items || [];
      if (!rows.length) {
        notify.info(MESSAGES.noDataToExport);
        return;
      }
      exportHpsList(rows, { format, sheetName: exportName });
    } catch (error) {
      notify.apiError(error, 'export HPS data');
    } finally {
      setIsExporting(false);
    }
  };

  const goToDetail = row => navigate(`/apps-hps/upd/${row.id}`);

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const statusOptions = STATUS_TABS.map(tab => ({
    value: tab.id,
    label: tab.label,
  }));
  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => setSearchText(''),
    },
    statusTab !== DEFAULT_STATUS_TAB && {
      key: 'status',
      label: 'Status',
      value: statusOptions.find(o => o.value === statusTab)?.label ?? statusTab,
      onRemove: () => handleStatusFilter(DEFAULT_STATUS_TAB),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);
  const sortColumnId =
    Object.keys(HPS_SORT_FIELDS).find(
      id => HPS_SORT_FIELDS[id] === sortField
    ) || sortField;

  const emptyState = (
    <EmptyState
      title='No HPS found'
      emptyHint='HPS requests appear here after they are sent from a procurement in Tender Opening.'
      filteredHint='Nothing matches the current search or status filter.'
      hasFilters={hasFilters}
      onClear={clearFilters}
      secondaryAction={
        can('canRead', '/apps-tenderopening/view')
          ? {
              label: 'Go to Tender Opening',
              icon: 'ri-arrow-right-line',
              onClick: () => navigate('/apps-tenderopening/view'),
            }
          : undefined
      }
    />
  );

  const columns = [
    {
      id: 'procurementnumber',
      name: 'ID Proc',
      selector: row => row.procurementNumber,
      cell: row => (
        <span className='to-id'>{row.procurementNumber || '-'}</span>
      ),
      width: '10%',
      sortable: true,
    },
    {
      id: 'hpsRequestDate',
      name: 'Submitted Date',
      selector: row => row.hpsRequestDate,
      cell: row => formatDateToDDMMYYYY(row.hpsRequestDate) || '-',
      width: '10%',
      sortable: true,
    },
    {
      id: 'hpsnumber',
      name: 'HPS No',
      selector: row => row.hpsRequestNumber,
      cell: row => <span className='to-id'>{row.hpsRequestNumber || '-'}</span>,
      width: '10%',
      sortable: true,
    },
    {
      id: 'titleprocurement',
      name: 'Procurement',
      selector: row => row.titleProcurement,
      cell: row => (
        <div className='d-flex align-items-center gap-2 py-2 w-100'>
          <ProductAvatar name={row.productType?.name} />
          <div className='pv-text-col flex-grow-1'>
            <div className='to-title' title={row.titleProcurement}>
              {row.titleProcurement || '-'}
            </div>
            <ProductInfo item={row} />
          </div>
        </div>
      ),
      width: '25%',
      sortable: true,
    },
    {
      id: 'procurementType',
      name: 'Proc Type',
      cell: row => (
        <span className='to-tag'>
          {HPS_PROCUREMENT_TYPE_MAP[row.procurementType] || '-'}
        </span>
      ),
      width: '13%',
    },
    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      cell: row => (
        <span className='pv-desc' title={row.description || ''}>
          {row.description || '-'}
        </span>
      ),
      width: '17%',
      sortable: true,
    },
    {
      id: 'statusHPS2',
      name: 'Status',
      selector: row => row.statusHPS2,
      cell: row => <StatusPill row={row} />,
      width: '10%',
      sortable: true,
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-arrow-right-up-line'
            label={
              row.procurementNumber
                ? `Open detail ${row.procurementNumber}`
                : 'Open detail'
            }
            onClick={e => {
              e.stopPropagation();
              goToDetail(row);
            }}
          />
        </div>
      ),
      width: '5%',
      center: true,
    },
  ];

  const MobileCard = ({ item }) => (
    <div
      className={classnames(
        'to-mcard d-md-none',
        isHpsAvailable(item) ? 'to-mcard--success' : 'to-mcard--warning'
      )}
    >
      <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
        <span className='to-id'>{item.procurementNumber || '-'}</span>
        <StatusPill row={item} />
      </div>

      <div className='d-flex align-items-center gap-2'>
        <ProductAvatar name={item.productType?.name} />
        <div className='pv-text-col flex-grow-1'>
          <div className='to-title'>{item.titleProcurement || '-'}</div>
          <ProductInfo item={item} />
        </div>
      </div>

      <div className='to-mcard-meta'>
        <div>
          <span>HPS No</span>
          <span>{item.hpsRequestNumber || '-'}</span>
        </div>
        <div>
          <span>Submitted</span>
          <span>{formatDateToDDMMYYYY(item.hpsRequestDate) || '-'}</span>
        </div>
        <div>
          <span>Proc Type</span>
          <span>{HPS_PROCUREMENT_TYPE_MAP[item.procurementType] || '-'}</span>
        </div>
      </div>

      {item.description && (
        <p className='pv-desc mb-3' title={item.description}>
          {item.description}
        </p>
      )}

      <div className='d-flex gap-2'>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-eye-line'
          className='flex-fill'
          onClick={() => goToDetail(item)}
        >
          View Detail
        </ActionButton>
      </div>
    </div>
  );

  return (
    <>
      <Row className='g-3 mb-3'>
        {STAT_CARDS.map(card => (
          <Col key={card.key} xs={12} sm={6} lg={3}>
            <StatCard
              card={card}
              summary={summary}
              isActive={statusTab === card.tabId}
              onSelect={() => handleStatusFilter(card.tabId)}
            />
          </Col>
        ))}
      </Row>

      <div className='to-panel'>
        <div className='to-toolbar'>
          <ListSearch
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder='Search by title or HPS no...'
            ariaLabel='Search HPS'
          />
          <ListFilterSelect
            label='Status'
            value={statusTab}
            onChange={handleStatusFilter}
            allLabel='All Status'
            emptyValue={DEFAULT_STATUS_TAB}
            options={statusOptions}
          />
          <ClearFiltersButton
            count={filterChips.length}
            onClick={clearFilters}
          />
          <span className='to-toolbar-meta'>
            {isLoading
              ? 'Loading...'
              : `Showing ${startEntry}–${endEntry} of ${totalRows} HPS`}
          </span>
          <UncontrolledDropdown>
            <DropdownToggle
              tag='button'
              type='button'
              className='btn btn-light btn-sm d-inline-flex align-items-center'
              disabled={isExporting || isLoading || !totalRows}
            >
              {isExporting ? (
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                />
              ) : (
                <i className='ri-download-2-line me-2' aria-hidden='true' />
              )}
              Export
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => handleExport('xlsx')}>
                <i className='ri-file-excel-2-line align-middle me-2 text-success'></i>
                Export to Excel
              </DropdownItem>
              <DropdownItem onClick={() => handleExport('csv')}>
                <i className='ri-file-text-line align-middle me-2 text-muted'></i>
                Export to CSV
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>

        <ActiveFilterChips chips={filterChips} />

        <div className='d-md-none p-3'>
          {isLoading && <SkeletonRows count={3} />}
          {!isLoading &&
            items.map((item, index) => (
              <MobileCard key={item.id || index} item={item} />
            ))}
          {!isLoading && !items.length && emptyState}
        </div>

        <div className='d-none d-md-block to-table-wrap'>
          <DataTable
            key={`sort-${sortColumnId}-${sortDirection}`}
            columns={columns}
            data={items}
            pagination={false}
            onSort={handleSort}
            sortServer
            defaultSortFieldId={sortColumnId}
            defaultSortAsc={sortDirection === 'asc'}
            onRowClicked={goToDetail}
            highlightOnHover
            pointerOnHover
            persistTableHead
            progressPending={isLoading}
            progressComponent={<SkeletonRows />}
            noDataComponent={emptyState}
            customStyles={{
              headCells: {
                style: { paddingLeft: '12px', paddingRight: '12px' },
              },
              cells: {
                style: {
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  wordBreak: 'break-word',
                },
              },
              table: { style: { width: '100%' } },
              tableWrapper: { style: { display: 'table' } },
            }}
          />
        </div>

        <div className='to-footer'>
          <div className='d-flex align-items-center gap-2'>
            <span>Rows per page</span>
            <select
              className='form-select form-select-sm'
              style={{ width: 'auto' }}
              value={rowsPerPage}
              onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
              aria-label='Rows per page'
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className='to-footer-range'>
              {startEntry}–{endEntry} of <strong>{totalRows}</strong> items
            </span>
          </div>

          <div className='to-pager'>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => setCurrentPage(1)}
              aria-label='First page'
              title='First page'
            >
              <i className='ri-skip-back-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => setCurrentPage(currentPage - 1)}
              aria-label='Previous page'
              title='Previous page'
            >
              <i className='ri-arrow-left-s-line' aria-hidden='true' />
            </button>
            <span className='to-pager-current'>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => setCurrentPage(currentPage + 1)}
              aria-label='Next page'
              title='Next page'
            >
              <i className='ri-arrow-right-s-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => setCurrentPage(totalPages)}
              aria-label='Last page'
              title='Last page'
            >
              <i className='ri-skip-forward-line' aria-hidden='true' />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(HpsTable);
